// =============================================================================
// SimSense Platform — Telemetry Detail Modal (OT-322)
// Quick single-lap telemetry inspection without navigating to Compare page.
// =============================================================================

import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { useUnits } from "../hooks/useUnits";
import { UPlotChart } from "./telemetry/UPlotChart";
import type { UPlotChartHandle } from "./telemetry/UPlotChart";
import uPlot from "uplot";

interface TelemetryDetailModalProps {
  sessionId: string;
  globalLap: number;
  lapNum: number;
  trackName: string;
  carName: string;
  onClose: () => void;
  onNavigate?: (globalLap: number) => void;
  availableLaps?: { globalLap: number; lapNum: number }[];
}

export function TelemetryDetailModal({
  sessionId, globalLap, lapNum, trackName, carName,
  onClose, onNavigate, availableLaps,
}: TelemetryDetailModalProps) {
  const [telemetry, setTelemetry] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const units = useUnits();
  const speedRef = useRef<UPlotChartHandle>(null);
  const inputRef = useRef<UPlotChartHandle>(null);
  const steerRef = useRef<UPlotChartHandle>(null);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Fetch telemetry
  useEffect(() => {
    setLoading(true);
    setError(null);
    api<any>(`/api/sessions/${sessionId}/laps/${globalLap}/telemetry`)
      .then(data => setTelemetry(data))
      .catch(err => setError(err?.message || "Failed to load telemetry"))
      .finally(() => setLoading(false));
  }, [sessionId, globalLap]);

  // Navigate to prev/next lap
  const currentIdx = availableLaps?.findIndex(l => l.globalLap === globalLap) ?? -1;
  const prevLap = currentIdx > 0 ? availableLaps![currentIdx - 1] : null;
  const nextLap = currentIdx >= 0 && currentIdx < (availableLaps?.length ?? 0) - 1 ? availableLaps![currentIdx + 1] : null;

  // Chart data
  const chartWidth = 900;
  const chartHeight = 140;

  // Data comes back already converted from the API (Throttle/Brake as 0-100, Speed in m/s)
  const speedData = useMemo((): uPlot.AlignedData | null => {
    if (!telemetry?.LapDistPct || !telemetry?.Speed) return null;
    const dist = telemetry.LapDistPct.map((v: number) => v > 1.5 ? v : v * 100);
    const speed = telemetry.Speed.map((v: number) => v * (units.system === "standard" ? 2.23694 : 3.6));
    return [dist, speed];
  }, [telemetry, units.system]);

  const throttleData = useMemo((): uPlot.AlignedData | null => {
    if (!telemetry?.LapDistPct || !telemetry?.Throttle) return null;
    const dist = telemetry.LapDistPct.map((v: number) => v > 1.5 ? v : v * 100);
    const maxThrottle = Math.max(...telemetry.Throttle);
    const scale = maxThrottle > 1.5 ? 1 : 100;
    const throttle = telemetry.Throttle.map((v: number) => v * scale);
    return [dist, throttle];
  }, [telemetry]);

  const brakeData = useMemo((): uPlot.AlignedData | null => {
    if (!telemetry?.LapDistPct || !telemetry?.Brake) return null;
    const dist = telemetry.LapDistPct.map((v: number) => v > 1.5 ? v : v * 100);
    const maxBrake = Math.max(...telemetry.Brake);
    const scale = maxBrake > 1.5 ? 1 : 100;
    const brake = telemetry.Brake.map((v: number) => v * scale);
    return [dist, brake];
  }, [telemetry]);

  const steerData = useMemo((): uPlot.AlignedData | null => {
    if (!telemetry?.LapDistPct || !telemetry?.SteeringWheelAngle) return null;
    const dist = telemetry.LapDistPct.map((v: number) => v > 1.5 ? v : v * 100);
    // SteeringWheelAngle: check if already in degrees or radians
    const maxSteer = Math.max(...telemetry.SteeringWheelAngle.map(Math.abs));
    const steer = maxSteer > 10
      ? telemetry.SteeringWheelAngle // already degrees
      : telemetry.SteeringWheelAngle.map((v: number) => v * 57.2958); // radians → degrees
    return [dist, steer];
  }, [telemetry]);

  const makeOpts = (label: string, color: string): uPlot.Options => ({
    width: chartWidth,
    height: chartHeight,
    cursor: { sync: { key: "telem-detail", setSeries: false }, points: { show: false } },
    legend: { show: false },
    scales: { x: { time: false } },
    axes: [
      { show: true, font: "10px sans-serif", stroke: "#475569", grid: { show: false }, ticks: { show: false }, size: 20 },
      { show: true, font: "10px sans-serif", stroke: "#475569", grid: { stroke: "rgba(255,255,255,0.05)", width: 1, dash: [2, 4] }, ticks: { show: false }, size: 36 },
    ],
    series: [
      {},
      { label, stroke: color, width: 1.5 },
    ],
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.85)", zIndex: 1000,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "20px", overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg2)", width: "100%", maxWidth: "1000px",
          borderRadius: "12px", border: "1px solid var(--border)",
          padding: "24px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7)",
          margin: "20px 0",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid var(--border)", paddingBottom: "12px" }}>
          <div>
            <h2 style={{ fontSize: "18px", margin: 0 }}>Lap {lapNum} Telemetry</h2>
            <div style={{ fontSize: "12px", color: "var(--text2)", marginTop: "2px" }}>{carName} @ {trackName}</div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {prevLap && (
              <button onClick={() => onNavigate?.(prevLap.globalLap)} style={{ background: "rgba(255,255,255,0.08)", border: "none", color: "var(--text1)", padding: "6px 12px", borderRadius: "4px", fontSize: "11px", cursor: "pointer" }}>
                ← Lap {prevLap.lapNum}
              </button>
            )}
            {nextLap && (
              <button onClick={() => onNavigate?.(nextLap.globalLap)} style={{ background: "rgba(255,255,255,0.08)", border: "none", color: "var(--text1)", padding: "6px 12px", borderRadius: "4px", fontSize: "11px", cursor: "pointer" }}>
                Lap {nextLap.lapNum} →
              </button>
            )}
            <Link to={`/session/${sessionId}/compare?laps=${globalLap}`} style={{ background: "var(--accent)", color: "#fff", padding: "6px 12px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, textDecoration: "none" }}>
              Full Compare
            </Link>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "20px", padding: "4px 8px" }}>✕</button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ padding: "60px", textAlign: "center", color: "var(--text2)" }}>
            Loading telemetry...
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--bad)" }}>{error}</div>
        )}

        {/* Charts */}
        {!loading && !error && telemetry && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Speed */}
            {speedData && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Speed</div>
                <UPlotChart ref={speedRef} data={speedData} options={makeOpts("Speed", "#3b82f6")} />
              </div>
            )}
            {/* Throttle */}
            {throttleData && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Throttle</div>
                <UPlotChart ref={inputRef} data={throttleData} options={makeOpts("Throttle", "#10b981")} />
              </div>
            )}
            {/* Brake */}
            {brakeData && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Brake</div>
                <UPlotChart data={brakeData} options={makeOpts("Brake", "#ef4444")} />
              </div>
            )}
            {/* Steering Angle */}
            {steerData && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.5px" }}>Steering Angle</div>
                <UPlotChart ref={steerRef} data={steerData} options={makeOpts("Steering", "#f59e0b")} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
